<template>
    <div class="row row-cols-1 row-cols-sm-2 row-cols-lg-3 survey-cards cards-container g-4 my-3">
        <SurveyCard v-for="survey in dataSet" class="col border-0" :key="'suvey_' + survey.id" :survey="survey" @download-survey="$event => openDownloadSurveyModal($event)" @delete-survey="$event => openDeleteModal($event)"></SurveyCard>

    </div>
	<div class="row justify-content-center" v-if="dataSet.length === 0 && !contentLoading">
		<div  class="col-12 col-md-6">
			<div class="empty-state-block d-flex flex-column align-items-center justify-content-center py-5 my-5">
				<i class="bi bi-inbox fs-1 mb-3"></i>
				<h4 class="mb-2">No Surveys Found</h4>
				<p class="text-muted text-center">We couldn't find any surveys matching your criteria. Please try adjusting your filters or check back later for new surveys.</p>
			</div>
		</div>
	</div>
    <div ref="infiniteLoaderContainer" class="d-none infinite-loader-container"></div>

    <ConfirmationModal v-if="showDownloadModal" ref="downloadModal" :title="modal_title_text" :info_text="modal_info_text" :icon_class="modal_icon_class" :btn_text="modal_btn_text" :btn_color="modal_btn_color" @close-modal="closeDownloadSurveyModal" @perform-action="downloadSurvey"></ConfirmationModal>

    <ConfirmationModal v-if="showDeleteModal" ref="deleteModal" :content-id="'deleteSurveyModalBlock'" :title="modal_title_text" :info_text="modal_info_text" :icon_class="modal_icon_class" :btn_text="modal_btn_text" :btn_color="modal_btn_color" @close-modal="closeDeleteModal" @perform-action="removeSurvey"></ConfirmationModal>
</template>

<script setup>
import SurveyCard from './SurveyCard.vue';
import { ref, computed, watch } from 'vue';
import { onMounted, onUnmounted } from 'vue';
import { useWindowSize } from '@vueuse/core';
import apiClient from "@/utils/axios";
import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import ConfirmationModal from '@/components/GenericModalAndViews/ConfirmationModal.vue';
import { useAppHelpers } from '@/composables/useAppHelpers';
import { useAuthStore } from "@/stores/auth-store.js";

const props = defineProps({
    filters: Object
});

const auth = useAuthStore();
const helper = useAppHelpers();

const noOfItems = ref(null);
const infiniteLoaderContainer = ref(null);
const contentLoading = ref(false);
const { width, height } = useWindowSize();
const itemsToLoad = computed(() => {
    // 5 per row
    if (width.value > 1599 && width.value < 1999)
        return 25 - (noOfItems.value % 5)
    // 7 per row
    if (width.value > 2499 && width.value < 2699)
        return 21 - (noOfItems.value % 7)
    // 9 per row
    if (width.value > 2999)
        return 27 - (noOfItems.value % 9)
    //1 2 3 4 6 8 = 24 LCM
    return 24 - (noOfItems.value % 24)
});

const params = ref(props.filters ? props.filters : {});
watch(() => props.filters, (val) => {
    params.value = val;
    allContentLoaded.value = false;
    if (!contentLoading.value && !allContentLoaded.value) {
        dataSet.value = [];
        contentLoading.value = true;
        helper.loaderOn(infiniteLoaderContainer.value, true);
        loadContent();
    }
});

const dataSet = ref([]);
const allContentLoaded = ref(false);
const fetchSurveys = async () => {
    let url = "member/surveys"
    try {
        params.value['limit'] = itemsToLoad.value;
        params.value['offset'] = dataSet.value.length;
        const content = await apiClient.get(url, { params: params.value });
        if (content.data.data) {
            let contentLoaded = content.data.data.length;
            if (contentLoaded == 0 || contentLoaded < itemsToLoad.value) {
                allContentLoaded.value = true;
            }
            content.data.data.forEach((d) => {
                dataSet.value.push(d);
            });
        }

        setTimeout(() => {
            helper.loaderOff(infiniteLoaderContainer.value, true);
            contentLoading.value = false;
        }, 1000);
    }
    catch (error) {
        setTimeout(() => {
            helper.loaderOff(infiniteLoaderContainer.value, true);
            contentLoading.value = false;
        }, 1000);
        let errorObj = await useHttpErrorManager().handleError(error, false);
    }
}

const infiniteContentLoadHandler = () => {
    if (!contentLoading.value && !allContentLoaded.value && window.scrollY + window.innerHeight >= document.documentElement.scrollHeight) {
        contentLoading.value = true;
        helper.loaderOn(infiniteLoaderContainer.value, true);
        fetchSurveys();
    }
}

const modal_title_text = ref('');
const modal_info_text = ref('');
const modal_icon_class = ref('');
const modal_btn_text = ref('');
const modal_btn_color = ref('');

const downloadModal = ref(null);
const showDownloadModal = ref(false);
const downloadSurveyId = ref(null);
const openDownloadSurveyModal = (id) => {
    downloadSurveyId.value = id;
    modal_title_text.value = "Download Survey";
    modal_info_text.value = "Are you sure you want to download the data of this survey!";
    modal_btn_text.value = "Proceed to Download!";
    modal_icon_class.value = "bi-cloud-arrow-down text-primary";
    modal_btn_color.value = 'btn-dark';
    showDownloadModal.value = true;
}
const closeDownloadSurveyModal = () => {
    downloadSurveyId.value = null;
    modal_title_text.value = "";
    modal_info_text.value = "";
    modal_btn_text.value = "";
    modal_icon_class.value = "";
    modal_btn_color.value = '';
    showDownloadModal.value = false;
}
const downloadSurvey = async () => {
    if (downloadSurveyId.value) {
        let url = "member/surveys/download/" + downloadSurveyId.value;
        try {
            let response = await apiClient.post(url, {}, { responseType: 'blob' });
            if (response.status === 200) {
                var disposition = response.headers['content-disposition'];
                var matches = /"([^"]*)"/.exec(disposition);
                var filename = matches && matches[1] ? matches[1] : 'survey_data_' + downloadSurveyId.value + '.xlsx';

                var blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                var link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = filename;

                document.body.appendChild(link);

                link.click();
                document.body.removeChild(link);
            }
            downloadModal.value.close();
        }
        catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
            downloadModal.value.close();
        }
    }
    else {
        downloadModal.value.close();
    }
}

const deleteModal = ref(null);
const showDeleteModal = ref(false);
const deleteSurveyId = ref(null);
const openDeleteModal = (id) => {
    deleteSurveyId.value = id;
    modal_title_text.value = "Delete Survey Consent";
    modal_info_text.value = "You are requesting to permanently delete a survey you have already submitted. This action will remove your responses from our records and they will no longer be used for research or analysis. Please note that this action is irreversible. You can exit this process at any time before confirming deletion.<br \><br \>This will delete all your survey data for this particular survey from our system and neither you or anyone else will be able to retrieve it.<br \>Are you sure you want to delete your data?";
    modal_btn_text.value = "Yes, Confirm Deletion!";
    modal_icon_class.value = "bi-trash text-danger";
    modal_btn_color.value = 'btn-danger';
    showDeleteModal.value = true;
}
const closeDeleteModal = () => {
    deleteSurveyId.value = null;
    modal_title_text.value = "";
    modal_info_text.value = "";
    modal_btn_text.value = "";
    modal_icon_class.value = "";
    modal_btn_color.value = '';
    showDeleteModal.value = false;
}

const removeSurvey = async () => {
	if(!auth.isLoggedIn) {
		await router.push({path: '/'});
		return true;
	}
	if(deleteSurveyId.value) {
		let deleteSurveyModalBlock = document.getElementById('deleteSurveyModalBlock');
		helper.loaderOn(deleteSurveyModalBlock, false, true);
		try {
			let response = await apiClient.post('member/surveys/removeSurvey', {
				surveyId: deleteSurveyId.value,
				consentGiven: true
			});
			if(response.status === 200) {
				const indexToDelete = dataSet.value.findIndex(item => item.uuid === deleteSurveyId.value);
				if (indexToDelete !== -1) {
					dataSet.value.splice(indexToDelete, 1);
				}
				deleteModal.value.close();
			}
		}
		catch(error) {
			let errorObj = await useHttpErrorManager().handleError(error, true);
		}
		finally {
			helper.loaderOff(deleteSurveyModalBlock);
		}
	}
}

onMounted(() => {
    if (width.value > 1599 && width.value < 1999)
        noOfItems.value = 25;
    else if (width.value > 2499 && width.value < 2699)
        noOfItems.value = 21;
    else if (width.value > 2999)
        noOfItems.value = 27;
    else
        noOfItems.value = 24;

    window.addEventListener('scroll', infiniteContentLoadHandler);

    contentLoading.value = true;
    helper.loaderOn(infiniteLoaderContainer.value, true);
    fetchSurveys();
});

onUnmounted(() => {
    window.removeEventListener('scroll', infiniteContentLoadHandler);
})
</script>
